/*
 * Plan Builder scoring — ported VERBATIM from
 * artifacts/prototypes/motr-improvement-plan-v1.html `scoreAll()`.
 *
 * Math is treated as load-bearing: thresholds, driver mapping, joy
 * formula and weakest/strongest selection MUST match the prototype so
 * the production tool produces identical action plans for identical
 * inputs.
 *
 * Moore maturity stage:  avg(op1..op5)
 *                        ≤1.8 → 1, ≤2.6 → 2, ≤3.4 → 3, else 4
 * IHI drivers:           exp1→autonomy, exp2→safety, exp3→meaning,
 *                        exp4→participative, exp5→improvement,
 *                        exp6→recognition
 * Weakest:               two lowest-scoring drivers (ASC sort)
 * Strongest:             highest-scoring driver
 * Joy score:             sum / (6 × 4) × 100 (rounded)
 * Alignment gap:         gap1 ≤ 2
 * Pre-mortem:            keyed off gap3
 */
import type {
  DiagnosticAnswers,
  IhiDriver,
  LikertExp,
  ScoreResult,
} from './types';
import type { PlanBuilderConfig } from './config';


export function scoreAll(
  answers: DiagnosticAnswers,
  config: PlanBuilderConfig,
): ScoreResult {
  // Operational maturity — average of op1..op5. Missing = 1, matching
  // the prototype's `R[id] || 1` fallback.
  const opScores = [
    answers.op1 ?? 1,
    answers.op2 ?? 1,
    answers.op3 ?? 1,
    answers.op4 ?? 1,
    answers.op5 ?? 1,
  ];
  const opAvg = opScores.reduce((a, b) => a + b, 0) / opScores.length;
  const { s1Max, s2Max, s3Max } = config.scoring.stageThresholds;
  const stage: 1 | 2 | 3 | 4 =
    opAvg <= s1Max ? 1 : opAvg <= s2Max ? 2 : opAvg <= s3Max ? 3 : 4;

  // IHI driver scores — pull each exp question to its mapped driver.
  const driverScores: Record<IhiDriver, LikertExp> = {
    autonomy: (answers.exp1 ?? 1) as LikertExp,
    safety: (answers.exp2 ?? 1) as LikertExp,
    meaning: (answers.exp3 ?? 1) as LikertExp,
    participative: (answers.exp4 ?? 1) as LikertExp,
    improvement: (answers.exp5 ?? 1) as LikertExp,
    recognition: (answers.exp6 ?? 1) as LikertExp,
  };

  const driverEntries = Object.entries(driverScores) as Array<
    [IhiDriver, LikertExp]
  >;
  const sorted = [...driverEntries].sort(([, a], [, b]) => a - b);
  const weakest = sorted.slice(0, 2).map(([d]) => d);
  // The prototype takes the LAST entry of the ascending sort as
  // "strongest" — equivalent to the max.
  const strongest = sorted[sorted.length - 1][0];

  const sum = driverEntries.reduce((acc, [, v]) => acc + v, 0);
  const joyScore = Math.round((sum / (driverEntries.length * 4)) * 100);

  return {
    stage,
    stageData: { id: stage, ...config.scoring.stages[stage] },
    driverScores,
    weakest,
    strongest,
    alignmentGap: (answers.gap1 ?? 3) <= 2,
    preMortem: answers.gap3,
    joyScore,
  };
}
